// NYSE trading-session calculator. Pure + timezone-correct via Intl, so it
// runs identically on the server and in the browser. Reproduces the prototype's
// "NYSE OPEN/CLOSED" indicator with pre/after-hours and holiday awareness.

import { APP_LOCALE } from "@/platform/kit";
import { utcToZonedParts, zoneOffsetMinutes, type ZonedParts } from "@/platform/kit";

type MarketStatus = "open" | "pre" | "after" | "closed" | "holiday" | "weekend";

interface MarketSession {
  status: MarketStatus;
  isOpen: boolean;
  label: string;
  /** Wall-clock time at the exchange, e.g. "13:21". */
  exchangeTime: string;
  /** Minutes until the next open/close transition, when meaningful. */
  reason: string;
}

const ET_TZ = "America/New_York";

// Exchange wall-clock parts, via the shared timezone kernel (`platform/kit/tz.ts`).
function getEtParts(date: Date, tz = ET_TZ): ZonedParts {
  return utcToZonedParts(date, tz);
}

const pad = (n: number) => String(n).padStart(2, "0");
const ymd = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;

function nthWeekdayOfMonth(year: number, month: number, weekday: number, n: number): string {
  // weekday: 0=Sun..6=Sat ; month 1-12
  const first = new Date(Date.UTC(year, month - 1, 1));
  const firstDow = first.getUTCDay();
  const day = 1 + ((weekday - firstDow + 7) % 7) + (n - 1) * 7;
  return ymd(year, month, day);
}

function lastWeekdayOfMonth(year: number, month: number, weekday: number): string {
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const lastDow = new Date(Date.UTC(year, month - 1, last)).getUTCDay();
  const day = last - ((lastDow - weekday + 7) % 7);
  return ymd(year, month, day);
}

function easterSunday(year: number): string {
  // Anonymous Gregorian algorithm
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return ymd(year, month, day);
}

function addDays(dateStr: string, delta: number): string {
  const [y, m, d] = dateStr.split("-").map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d + delta));
  return ymd(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
}

/** Shift a fixed-date holiday to the NYSE observed day (Sat→Fri, Sun→Mon). */
function observed(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map(Number) as [number, number, number];
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  if (dow === 6) return addDays(dateStr, -1);
  if (dow === 0) return addDays(dateStr, 1);
  return dateStr;
}

export function nyseHolidays(year: number): Set<string> {
  const h = new Set<string>();
  h.add(observed(ymd(year, 1, 1))); // New Year's Day
  h.add(nthWeekdayOfMonth(year, 1, 1, 3)); // MLK Jr Day — 3rd Mon Jan
  h.add(nthWeekdayOfMonth(year, 2, 1, 3)); // Washington's Birthday — 3rd Mon Feb
  h.add(addDays(easterSunday(year), -2)); // Good Friday
  h.add(lastWeekdayOfMonth(year, 5, 1)); // Memorial Day — last Mon May
  if (year >= 2022) h.add(observed(ymd(year, 6, 19))); // Juneteenth
  h.add(observed(ymd(year, 7, 4))); // Independence Day
  h.add(nthWeekdayOfMonth(year, 9, 1, 1)); // Labor Day — 1st Mon Sep
  h.add(nthWeekdayOfMonth(year, 11, 4, 4)); // Thanksgiving — 4th Thu Nov
  h.add(observed(ymd(year, 12, 25))); // Christmas
  return h;
}

/** Days the NYSE closes early at 13:00 ET. */
export function nyseEarlyCloses(year: number): Set<string> {
  const s = new Set<string>();
  // Day after Thanksgiving
  s.add(addDays(nthWeekdayOfMonth(year, 11, 4, 4), 1));
  // Christmas Eve and July 3, when they fall on a weekday
  for (const ds of [ymd(year, 12, 24), ymd(year, 7, 3)]) {
    const [y, m, d] = ds.split("-").map(Number) as [number, number, number];
    const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
    if (dow >= 1 && dow <= 5) s.add(ds);
  }
  return s;
}

const REG_OPEN = 9 * 60 + 30; // 09:30
const REG_CLOSE = 16 * 60; // 16:00
const EARLY_CLOSE = 13 * 60; // 13:00
const PRE_OPEN = 4 * 60; // 04:00 pre-market

export function getMarketSession(now: Date = new Date()): MarketSession {
  const et = getEtParts(now);
  const exchangeTime = `${pad(et.hour)}:${pad(et.minute)}`;
  const dateStr = ymd(et.year, et.month, et.day);
  const minutes = et.hour * 60 + et.minute;

  if (et.weekday === 0 || et.weekday === 6) {
    return mk("weekend", false, "Weekend", exchangeTime, "Closed for the weekend");
  }
  if (nyseHolidays(et.year).has(dateStr)) {
    return mk("holiday", false, "Holiday", exchangeTime, "Market holiday");
  }

  const close = nyseEarlyCloses(et.year).has(dateStr) ? EARLY_CLOSE : REG_CLOSE;

  if (minutes >= REG_OPEN && minutes < close) {
    return mk("open", true, "Open", exchangeTime, "Regular trading");
  }
  if (minutes >= PRE_OPEN && minutes < REG_OPEN) {
    return mk("pre", false, "Pre-market", exchangeTime, "Opens 09:30 ET");
  }
  if (minutes >= close && minutes < 20 * 60) {
    return mk("after", false, "After hours", exchangeTime, "Closed 16:00 ET");
  }
  return mk("closed", false, "Closed", exchangeTime, "Opens 09:30 ET");
}

function mk(
  status: MarketStatus,
  isOpen: boolean,
  label: string,
  exchangeTime: string,
  reason: string,
): MarketSession {
  return { status, isOpen, label, exchangeTime, reason };
}

// ---------------------------------------------------------------------------
// Rich clock detail for the header popover (mirrors the prototype): countdown
// to the next open/close and that event's time in both NY and the local zone.
// ---------------------------------------------------------------------------
interface MarketClock {
  session: MarketSession;
  nyseTime: string; // HH:MM at the exchange
  nyseDate: string; // e.g. "Mon, Jun 23" at the exchange
  localTime: string; // HH:MM local
  localDate: string;
  tzName: string; // short local zone name, e.g. "Paris"
  dotColor: string;
  reason: string;
  countdownLabel: string; // "Opens in" | "Closes in"
  countdown: string; // "2h 5m"
  eventTs: number; // epoch ms of the next open/close transition
  eventLabel: string; // "Opens" | "Closes"
  eventEtDay: string;
  eventEt: string;
  eventLocal: string;
}

function isTradingDay(et: { year: number; month: number; day: number; weekday: number }): boolean {
  if (et.weekday === 0 || et.weekday === 6) return false;
  return !nyseHolidays(et.year).has(ymd(et.year, et.month, et.day));
}

/** Whether a plain `YYYY-MM-DD` civil date is an NYSE trading day (not a weekend/holiday). */
export function isNyseTradingDay(dateStr: string): boolean {
  const [y, m, d] = dateStr.split("-").map(Number) as [number, number, number];
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  if (dow === 0 || dow === 6) return false;
  return !nyseHolidays(y).has(dateStr);
}

/** Roll a `YYYY-MM-DD` civil date to the nearest NYSE trading day, inclusive. */
export function nearestTradingDay(
  dateStr: string,
  direction: "forward" | "backward" = "forward",
): string {
  let d = dateStr;
  const step = direction === "forward" ? 1 : -1;
  while (!isNyseTradingDay(d)) d = addDays(d, step);
  return d;
}

function fmtDuration(ms: number): string {
  const tot = Math.max(0, Math.round(ms / 60000));
  const d = Math.floor(tot / 1440);
  const h = Math.floor((tot % 1440) / 60);
  const mi = tot % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${mi}m`;
  return `${mi}m`;
}

export function getMarketClock(now: Date = new Date(), localTz?: string): MarketClock {
  const session = getMarketSession(now);
  const et = getEtParts(now);
  const minutes = et.hour * 60 + et.minute;
  const close = nyseEarlyCloses(et.year).has(ymd(et.year, et.month, et.day)) ? 13 * 60 : REG_CLOSE;

  const fmtET = (opts: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(APP_LOCALE, { timeZone: ET_TZ, hour12: false, ...opts });
  // Pin the locale so the local clock's date reads the same way as the NYSE one
  // ("Tue, 30 Jun"), rather than following the browser locale.
  const localOpts = (opts: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(APP_LOCALE, {
      hour12: false,
      ...opts,
      ...(localTz ? { timeZone: localTz } : {}),
    });

  const etToTs = (y: number, m: number, d: number, hh: number, mm: number) => {
    const off = zoneOffsetMinutes(new Date(Date.UTC(y, m - 1, d, 12, 0, 0)), ET_TZ);
    return Date.UTC(y, m - 1, d, hh, mm) - off * 60000;
  };

  let eventTs: number;
  let eventLabel: string;
  let countdownLabel: string;
  if (session.isOpen) {
    eventTs = etToTs(et.year, et.month, et.day, Math.floor(close / 60), close % 60);
    eventLabel = "Closes";
    countdownLabel = "Closes in";
  } else {
    let found = etToTs(et.year, et.month, et.day, 9, 30);
    for (let off = 0; off <= 12; off++) {
      const p = getEtParts(new Date(now.getTime() + off * 86400000));
      if (!isTradingDay(p)) continue;
      if (off === 0 && minutes >= REG_OPEN) continue;
      found = etToTs(p.year, p.month, p.day, 9, 30);
      break;
    }
    eventTs = found;
    eventLabel = "Opens";
    countdownLabel = "Opens in";
  }

  let tzName = "Local";
  try {
    const z = localTz ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (z) tzName = z.split("/").pop()!.replace(/_/g, " ");
  } catch {
    /* keep default */
  }

  return {
    session,
    nyseTime: fmtET({ hour: "2-digit", minute: "2-digit" }).format(now),
    nyseDate: fmtET({ weekday: "short", month: "short", day: "numeric" }).format(now),
    localTime: localOpts({ hour: "2-digit", minute: "2-digit" }).format(now),
    localDate: localOpts({ weekday: "short", month: "short", day: "numeric" }).format(now),
    tzName,
    dotColor: session.isOpen ? "#46c08a" : "#6a6a70",
    reason: clockReason(session),
    countdownLabel,
    countdown: fmtDuration(eventTs - now.getTime()),
    eventTs,
    eventLabel,
    eventEtDay: fmtET({ weekday: "short", month: "short", day: "numeric" }).format(
      new Date(eventTs),
    ),
    eventEt: fmtET({ hour: "2-digit", minute: "2-digit" }).format(new Date(eventTs)),
    eventLocal: localOpts({ hour: "2-digit", minute: "2-digit" }).format(new Date(eventTs)),
  };
}

function clockReason(session: MarketSession): string {
  switch (session.status) {
    case "open":
      return "Regular session";
    case "weekend":
      return "Weekend";
    case "holiday":
      return "Market holiday";
    case "pre":
      return "Pre-market";
    case "after":
      return "After hours";
    default:
      return "Closed";
  }
}
